import { CHUNK_SIZE, TILE_SIZE } from '@dreamgame/shared';
import { Graphics } from 'pixi.js';
import { getDebugFlags } from '../../state/debug';
import { allPawns, getInterpolatedPosition } from '../../state/entities';
import { getCamera, getScreenDimensions } from '../Camera';
import { COLORS } from '../constants';
import type { VisibleChunk } from './types';

// ============================================================================
// DebugLayer - Renders debug overlays (grid, chunk borders, FPS)
// ============================================================================

let worldGraphics: Graphics; // World-space debug graphics
let uiGraphics: Graphics; // Screen-space debug UI

// FPS tracking
let frameCount = 0;
let fps = 0;
let lastFpsUpdate = performance.now();

export const createDebugLayer = (): { worldGraphics: Graphics; uiGraphics: Graphics } => {
  worldGraphics = new Graphics();
  uiGraphics = new Graphics();
  return { worldGraphics, uiGraphics };
};

export const drawDebugOverlays = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  worldGraphics.clear();

  const flags = getDebugFlags();

  if (flags.showChunkBorders) {
    drawChunkBorders({ visibleChunks });
  }

  if (flags.showOrigin) {
    drawOriginMarker();
  }

  if (flags.showTileGrid) {
    drawTileGrid();
  }

  if (flags.showEntityIds) {
    drawEntityDebugInfo();
  }
};

export const drawDebugHUD = (): void => {
  uiGraphics.clear();

  const flags = getDebugFlags();
  if (!flags.showPerformance) {
    return;
  }

  // Calculate FPS
  const now = performance.now();
  frameCount++;

  if (now - lastFpsUpdate >= 1000) {
    fps = frameCount;
    frameCount = 0;
    lastFpsUpdate = now;
  }

  // Draw debug HUD background
  const hudX = 10;
  const hudY = 10;
  const hudWidth = 180;
  const hudHeight = 100;

  uiGraphics.setFillStyle({ color: 0x000000, alpha: 0.7 });
  uiGraphics.roundRect(hudX, hudY, hudWidth, hudHeight, 6);
  uiGraphics.fill();

  uiGraphics.setStrokeStyle({ width: 1, color: 0x444444 });
  uiGraphics.roundRect(hudX, hudY, hudWidth, hudHeight, 6);
  uiGraphics.stroke();

  // Draw stats as colored indicators
  const lineHeight = 18;
  const startY = hudY + 12;

  // FPS indicator
  const fpsColor = fps >= 55 ? 0x44ff44 : fps >= 30 ? 0xffff44 : 0xff4444;
  uiGraphics.setFillStyle({ color: fpsColor });
  uiGraphics.circle(hudX + 14, startY + 4, 5);
  uiGraphics.fill();

  // Camera position indicator
  uiGraphics.setFillStyle({ color: 0x4488ff });
  uiGraphics.circle(hudX + 14, startY + lineHeight + 4, 5);
  uiGraphics.fill();

  // Zoom indicator
  uiGraphics.setFillStyle({ color: 0xff88ff });
  uiGraphics.circle(hudX + 14, startY + lineHeight * 2 + 4, 5);
  uiGraphics.fill();

  // Chunks loaded indicator
  uiGraphics.setFillStyle({ color: 0xffaa44 });
  uiGraphics.circle(hudX + 14, startY + lineHeight * 3 + 4, 5);
  uiGraphics.fill();

  // Draw FPS bar
  const fpsBarWidth = Math.min((fps / 60) * 100, 100);
  uiGraphics.setFillStyle({ color: fpsColor, alpha: 0.5 });
  uiGraphics.rect(hudX + 70, startY, fpsBarWidth, 10);
  uiGraphics.fill();
};

const drawChunkBorders = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  worldGraphics.setStrokeStyle({ width: 2, color: COLORS.CHUNK_BORDER, alpha: 0.7 });

  for (const chunk of visibleChunks) {
    const worldX = chunk.x * CHUNK_SIZE * TILE_SIZE;
    const worldY = chunk.y * CHUNK_SIZE * TILE_SIZE;
    const size = CHUNK_SIZE * TILE_SIZE;

    worldGraphics.rect(worldX, worldY, size, size);
    worldGraphics.stroke();

    // Chunk coordinates label background
    worldGraphics.setFillStyle({ color: COLORS.CHUNK_BORDER, alpha: 0.8 });
    const labelX = worldX + 8;
    const labelY = worldY + 8;
    worldGraphics.roundRect(labelX - 4, labelY - 4, 80, 20, 4);
    worldGraphics.fill();
  }
};

const drawOriginMarker = (): void => {
  const crossSize = 20;

  // Red X axis
  worldGraphics.setStrokeStyle({ width: 2, color: COLORS.ORIGIN_X });
  worldGraphics.moveTo(-crossSize, 0);
  worldGraphics.lineTo(crossSize * 2, 0);
  worldGraphics.stroke();

  // Green Y axis
  worldGraphics.setStrokeStyle({ width: 2, color: COLORS.ORIGIN_Y });
  worldGraphics.moveTo(0, -crossSize);
  worldGraphics.lineTo(0, crossSize * 2);
  worldGraphics.stroke();

  // Origin dot
  worldGraphics.setFillStyle({ color: 0xffffff });
  worldGraphics.circle(0, 0, 4);
  worldGraphics.fill();
};

const drawTileGrid = (): void => {
  const camera = getCamera();
  const { width: screenWidth, height: screenHeight } = getScreenDimensions();

  const halfWidth = screenWidth / 2 / camera.zoom;
  const halfHeight = screenHeight / 2 / camera.zoom;

  const minTileX = Math.floor((camera.x - halfWidth) / TILE_SIZE);
  const maxTileX = Math.ceil((camera.x + halfWidth) / TILE_SIZE);
  const minTileY = Math.floor((camera.y - halfHeight) / TILE_SIZE);
  const maxTileY = Math.ceil((camera.y + halfHeight) / TILE_SIZE);

  worldGraphics.setStrokeStyle({ width: 1, color: COLORS.TILE_GRID, alpha: 0.3 });

  // Vertical lines
  for (let x = minTileX; x <= maxTileX; x++) {
    const worldX = x * TILE_SIZE;
    worldGraphics.moveTo(worldX, minTileY * TILE_SIZE);
    worldGraphics.lineTo(worldX, maxTileY * TILE_SIZE);
    worldGraphics.stroke();
  }

  // Horizontal lines
  for (let y = minTileY; y <= maxTileY; y++) {
    const worldY = y * TILE_SIZE;
    worldGraphics.moveTo(minTileX * TILE_SIZE, worldY);
    worldGraphics.lineTo(maxTileX * TILE_SIZE, worldY);
    worldGraphics.stroke();
  }
};

const drawEntityDebugInfo = (): void => {
  const pawns = allPawns.value;

  for (const pawn of pawns) {
    const pos = getInterpolatedPosition({ pawn });
    const worldX = pos.x * TILE_SIZE;
    const worldY = pos.y * TILE_SIZE;

    // Draw entity ID background
    worldGraphics.setFillStyle({ color: 0x000000, alpha: 0.7 });
    worldGraphics.roundRect(worldX, worldY - 24, 32, 14, 2);
    worldGraphics.fill();
  }
};
